import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import { saveAs } from "file-saver";

interface LaboratoryReportState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  filter: {
    targetDates: Date[];
    lineType: string | null;
  };

  laboratoryData: any;
}

const state: LaboratoryReportState = {
  loading: false,
  loaded: false,
  fatalError: false,

  filter: {
    targetDates: [new Date(), new Date()],
    lineType: null,
  },

  laboratoryData: {
    atom: [],
    vdp: [],
  },
};

function buildFilter(params: any) {
  const queryParams: any = {};

  if (params.targetDates) {
    queryParams.date_from = moment(params.targetDates[0]).format("YYYY-MM-DD");
    queryParams.date_to = moment(params.targetDates[1])
      .add(1, "day")
      .format("YYYY-MM-DD");
  }

  if (params.lineType) {
    queryParams.line_type = params.lineType;
  }

  return queryParams;
}

export const REPORT_DATA_ATOM = "REPORT_DATA_ATOM";
export const REPORT_DATA_VDP = "REPORT_DATA_VDP";

export const actions: ActionTree<LaboratoryReportState, RootState> = {
  loadData({ commit, state, dispatch }, params) {
    const vm = (this as any)._vm;

    return new Promise<void>((resolve, reject) => {
      if (Object.values(params).length > 0) {
        commit("START_LOADING");

        const queries: any[] = [];

        // Запрос для АТОМ лаборатории
        if (!params.lineType || params.lineType === "atom") {
          queries.push(
            vm
              .$q(REPORT_DATA_ATOM)
              .raw("documents.list")
              .action({
                columns: {
                  id: "sal.id",
                  shift_id: "s.id",
                  shift_date: "s.shift_date",
                  line_name: "l.name",
                  datetime: "sal.datetime",
                  release_datetime: "sal.release_datetime",
                  plan_number: "sacp.number",
                  plan_number2: "sa.number",
                  produced_product_name: "pp.name",
                  product_name: "p.name",
                  base_name: "sacpi.name",
                  fact_datetime: "sja.bottling_end_date",
                  target_acidity: "pp.target_acidity",
                  journal_acidity_4: "sja.acidity_4",
                  acidity_4: "sacpm.acidity_4",
                  density_vpr: "sal.density_vpr",
                  speed_vpr: "sal.speed_vpr",
                  viscosity_vpr: "sal.viscosity_vpr",
                  resistance_vpr: "sal.resistance_vpr",
                  temp_vpr: "sal.temp_vpr",
                  acidity_ohaus_vpr: "sal.acidity_ohaus_vpr",
                  acidity_mettler_vpr: "sal.acidity_mettler_vpr",
                  density_gph: "sal.density_gph",
                  speed_gph: "sal.speed_gph",
                  viscosity_gph: "sal.viscosity_gph",
                  resistance_gph: "sal.resistance_gph",
                  temp_gph: "sal.temp_gph",
                  stirability_thermomix_name: "voc_stirability_thermomix.name",
                  stirability_thermomix_color:
                    "voc_stirability_thermomix.color",
                  glitter_thermomix_name: "voc_glitter_thermomix.name",
                  glitter_thermomix_color: "voc_glitter_thermomix.color",
                  thermal_stability_name: "voc_thermal_stability.name",
                  thermal_stability_color: "voc_thermal_stability.color",
                  heat_resistance_name: "voc_heat_resistance.name",
                  heat_resistance_color: "voc_heat_resistance.color",
                  hardness_name: "voc_hardness.name",
                  hardness_color: "voc_hardness.color",
                  causticity_initial_spalling_name:
                    "voc_causticity_initial_spalling.name",
                  causticity_initial_spalling_color:
                    "voc_causticity_initial_spalling.color",
                  plasticity_name: "voc_plasticity.name",
                  plasticity_color: "voc_plasticity.color",
                  looseness_name: "voc_looseness.name",
                  looseness_color: "voc_looseness.color",
                  sound_product_breaking_name:
                    "voc_sound_product_breaking.name",
                  sound_product_breaking_color:
                    "voc_sound_product_breaking.color",
                  sound_product_released_name:
                    "voc_sound_product_released.name",
                  sound_product_released_color:
                    "voc_sound_product_released.color",
                  feeling_of_texture_name: "voc_feeling_of_texture.name",
                  feeling_of_texture_color: "voc_feeling_of_texture.color",
                  fluff_effect_name: "voc_fluff_effect.name",
                  fluff_effect_color: "voc_fluff_effect.color",
                  creaminess_longitude: "sal.creaminess_longitude",
                  creaminess_height: "sal.creaminess_height",
                  border_creaminess_longitude: "pp.creaminess_longitude",
                  border_creaminess_height: "pp.creaminess_height",
                  kislinka_name: "voc_kislinka.name",
                  kislinka_color: "voc_kislinka.color",
                  serbian_profile_name: "voc_serbian_profile.name",
                  serbian_profile_color: "voc_serbian_profile.color",
                  sweetness_name: "voc_sweetness.name",
                  sweetness_color: "voc_sweetness.color",
                  salinity_name: "voc_salinity.name",
                  salinity_color: "voc_salinity.color",
                  vices_name: "voc_vices.name",
                  vices_color: "voc_vices.color",
                },
                from: "s:shift_atom_laboratory",
                from_as: "sal",
                joins: [
                  {
                    join: "s:shift_atom_coock_plan",
                    type: "left",
                    as: "sacp",
                    on: { $eq: ["sacp.id", "sal.plan_id"] },
                  },
                  {
                    join: "s:shifts",
                    type: "left",
                    as: "s",
                    on: { $eq: ["s.id", "sacp.shift_id"] },
                  },
                  {
                    join: "s:voc_lines",
                    type: "left",
                    as: "l",
                    on: { $eq: ["l.id", "s.line_id"] },
                  },
                  {
                    join: {
                      columns: {
                        coock_plan_id: "sacpi.coock_plan_id",
                        name: { $array_agg: "vb.name" },
                      },
                      from: "s:shift_atom_coock_plan_items",
                      from_as: "sacpi",
                      joins: [
                        {
                          join: "s:voc_bases",
                          type: "left",
                          as: "vb",
                          on: { $eq: ["vb.id", "sacpi.base_id"] },
                        },
                      ],
                      groups: ["sacpi.coock_plan_id"],
                    },
                    type: "left",
                    as: "sacpi",
                    on: { $eq: ["sacp.id", "sacpi.coock_plan_id"] },
                  },
                  {
                    join: "s:shift_atom",
                    type: "left",
                    as: "sa",
                    on: {
                      $eq: ["sal.bottling_plan_id", "sa.id"],
                    },
                  },
                  {
                    join: "s:products",
                    type: "left",
                    as: "p",
                    on: { $eq: ["sa.product_id", "p.id"] },
                  },
                  {
                    join: "s:produced_products",
                    type: "left",
                    as: "pp",
                    on: { $eq: ["pp.id", "sacp.product_id"] },
                  },
                  {
                    join: "s:shift_journal_atom",
                    type: "left",
                    as: "sja",
                    on: { $eq: ["sja.plan_id", "sacp.id"] },
                  },
                  {
                    join: "s:shift_atom_coock_plan_marks",
                    type: "left",
                    as: "sacpm",
                    on: { $eq: ["sacpm.plan_id", "sacp.id"] },
                  },
                  {
                    join: "s:voc_stirability_thermomix",
                    type: "left",
                    as: "voc_stirability_thermomix",
                    on: {
                      $eq: [
                        "sal.stirability_thermomix_id",
                        "voc_stirability_thermomix.id",
                      ],
                    },
                  },
                  {
                    join: "s:voc_glitter_thermomix",
                    type: "left",
                    as: "voc_glitter_thermomix",
                    on: {
                      $eq: [
                        "sal.glitter_thermomix_id",
                        "voc_glitter_thermomix.id",
                      ],
                    },
                  },
                  {
                    join: "s:voc_thermal_stability",
                    type: "left",
                    as: "voc_thermal_stability",
                    on: {
                      $eq: [
                        "sal.thermal_stability_id",
                        "voc_thermal_stability.id",
                      ],
                    },
                  },
                  {
                    join: "s:voc_heat_resistance",
                    type: "left",
                    as: "voc_heat_resistance",
                    on: {
                      $eq: ["sal.heat_resistance_id", "voc_heat_resistance.id"],
                    },
                  },
                  {
                    join: "s:voc_hardness",
                    type: "left",
                    as: "voc_hardness",
                    on: { $eq: ["sal.hardness_id", "voc_hardness.id"] },
                  },
                  {
                    join: "s:voc_causticity_initial_spalling",
                    type: "left",
                    as: "voc_causticity_initial_spalling",
                    on: {
                      $eq: [
                        "sal.causticity_initial_spalling_id",
                        "voc_causticity_initial_spalling.id",
                      ],
                    },
                  },
                  {
                    join: "s:voc_plasticity",
                    type: "left",
                    as: "voc_plasticity",
                    on: { $eq: ["sal.plasticity_id", "voc_plasticity.id"] },
                  },
                  {
                    join: "s:voc_looseness",
                    type: "left",
                    as: "voc_looseness",
                    on: { $eq: ["sal.looseness_id", "voc_looseness.id"] },
                  },
                  {
                    join: "s:voc_sound_product_breaking",
                    type: "left",
                    as: "voc_sound_product_breaking",
                    on: {
                      $eq: [
                        "sal.sound_product_breaking_id",
                        "voc_sound_product_breaking.id",
                      ],
                    },
                  },
                  {
                    join: "s:voc_sound_product_released",
                    type: "left",
                    as: "voc_sound_product_released",
                    on: {
                      $eq: [
                        "sal.sound_product_released_id",
                        "voc_sound_product_released.id",
                      ],
                    },
                  },
                  {
                    join: "s:voc_feeling_of_texture",
                    type: "left",
                    as: "voc_feeling_of_texture",
                    on: {
                      $eq: [
                        "sal.feeling_of_texture_id",
                        "voc_feeling_of_texture.id",
                      ],
                    },
                  },
                  {
                    join: "s:voc_fluff_effect",
                    type: "left",
                    as: "voc_fluff_effect",
                    on: { $eq: ["sal.fluff_effect_id", "voc_fluff_effect.id"] },
                  },
                  {
                    join: "s:voc_kislinka",
                    type: "left",
                    as: "voc_kislinka",
                    on: { $eq: ["sal.kislinka_id", "voc_kislinka.id"] },
                  },
                  {
                    join: "s:voc_serbian_profile",
                    type: "left",
                    as: "voc_serbian_profile",
                    on: {
                      $eq: ["sal.serbian_profile_id", "voc_serbian_profile.id"],
                    },
                  },
                  {
                    join: "s:voc_sweetness",
                    type: "left",
                    as: "voc_sweetness",
                    on: { $eq: ["sal.sweetness_id", "voc_sweetness.id"] },
                  },
                  {
                    join: "s:voc_salinity",
                    type: "left",
                    as: "voc_salinity",
                    on: { $eq: ["sal.salinity_id", "voc_salinity.id"] },
                  },
                  {
                    join: "s:voc_vices",
                    type: "left",
                    as: "voc_vices",
                    on: { $eq: ["sal.vices_id", "voc_vices.id"] },
                  },
                ],
                page_size: 10000,
                page: 1,
                order: [
                  { $desc: ["s.shift_date"] },
                  { $desc: ["sal.datetime"] },
                ],
                where: {
                  $and: [
                    {
                      $gte: [
                        "s.shift_date",
                        {
                          $: params.targetDates
                            ? moment(params.targetDates[0]).format("YYYY-MM-DD")
                            : moment().format("YYYY-MM-DD"),
                        },
                      ],
                    },
                    {
                      $lte: [
                        "s.shift_date",
                        {
                          $: params.targetDates
                            ? moment(params.targetDates[1]).format("YYYY-MM-DD")
                            : moment().format("YYYY-MM-DD"),
                        },
                      ],
                    },
                  ],
                },
              })
          );
        }

        // Запрос для ВДП лаборатории
        if (!params.lineType || params.lineType === "vdp") {
          queries.push(
            vm
              .$q(REPORT_DATA_VDP)
              .raw("documents.list")
              .action({
                columns: {
                  id: "svl.id",
                  shift_id: "s.id",
                  shift_date: "s.shift_date",
                  line_name: "l.name",
                  datetime: "svl.datetime",
                  plan_number: "spv.number",
                  base_name: "vb.name",
                  starter_type_name: "vst.name",
                  extract_time: "sfv.extract_time",
                  acidity_ohaus: "svl.acidity_ohaus",
                  acidity_mettler: "svl.acidity_mettler",
                  density_pp: "svl.density_pp",
                  speed: "svl.speed",
                  viscosity_pp: "svl.viscosity_pp",
                  resistance_pp: "svl.resistance_pp",
                  temp_pp: "svl.temp_pp",
                },
                from: "s:shift_vdp_laboratory",
                from_as: "svl",
                joins: [
                  {
                    join: "s:shift_plan_vdp",
                    type: "left",
                    as: "spv",
                    on: { $eq: ["svl.plan_id", "spv.id"] },
                  },
                  {
                    join: "s:shift_fact_vdp",
                    type: "left",
                    as: "sfv",
                    on: { $eq: ["sfv.shift_plan_id", "spv.id"] },
                  },
                  {
                    join: "s:shifts",
                    type: "left",
                    as: "s",
                    on: { $eq: ["spv.shift_id", "s.id"] },
                  },
                  {
                    join: "s:voc_lines",
                    type: "left",
                    as: "l",
                    on: { $eq: ["l.id", "s.line_id"] },
                  },
                  {
                    join: "s:voc_bases",
                    type: "left",
                    as: "vb",
                    on: { $eq: ["spv.base_id", "vb.id"] },
                  },
                  {
                    join: "s:voc_starter_types",
                    type: "left",
                    as: "vst",
                    on: { $eq: ["vst.id", "spv.starter_type_id"] },
                  },
                ],
                page_size: 10000,
                page: 1,
                order: [
                  { $desc: ["s.shift_date"] },
                  { $desc: ["svl.datetime"] },
                ],
                where: {
                  $and: [
                    {
                      $gte: [
                        "s.shift_date",
                        {
                          $: params.targetDates
                            ? moment(params.targetDates[0]).format("YYYY-MM-DD")
                            : moment().format("YYYY-MM-DD"),
                        },
                      ],
                    },
                    {
                      $lte: [
                        "s.shift_date",
                        {
                          $: params.targetDates
                            ? moment(params.targetDates[1]).format("YYYY-MM-DD")
                            : moment().format("YYYY-MM-DD"),
                        },
                      ],
                    },
                  ],
                },
              })
          );
        }

        vm.$db(queries)
          .then((result: ServerResult) => {
            const content: any = {
              atom: [],
              vdp: [],
            };

            if (!params.lineType || params.lineType === "atom") {
              const opAtom = result.getOperation(REPORT_DATA_ATOM);
              if (!opAtom.hasError()) {
                content.atom = opAtom
                  .getContent()
                  .documents.map((item: any) => {
                    if (item.base_name && Array.isArray(item.base_name)) {
                      item.base_name = item.base_name
                        .filter((value: any, index: any, array: any) => {
                          return array.indexOf(value) === index;
                        })
                        .join(", ");
                    }
                    return item;
                  });
              }
            }

            if (!params.lineType || params.lineType === "vdp") {
              const opVdp = result.getOperation(REPORT_DATA_VDP);
              if (!opVdp.hasError()) {
                content.vdp = opVdp.getContent().documents.map((item: any) => {
                  if (item.datetime && item.extract_time) {
                    const _start = moment("1970-01-01 00:00:00").add(
                      moment.duration(
                        moment(item.datetime).diff(moment(item.extract_time))
                      )
                    );
                    item.delay_time = _start.format("HH:mm:ss");
                  } else {
                    item.delay_time = null;
                  }
                  return item;
                });
              }
            }

            commit("SET_REPORT_DATA", content);
            commit("LOADED_SUCCESS");

            resolve();
          })
          .catch((error: any) => {
            commit("LOADED_ERROR");
            reject(error);
          });
      } else {
        resolve();
      }
    });
  },
};

export const mutations: MutationTree<LaboratoryReportState> = {
  START_LOADING: (state) => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_REPORT_DATA(state, data) {
    state.laboratoryData = data;
  },
};

const getters = {};

export const STORE_KEY = "$_laboratory_report";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
